import ajax from '../../common/services/httpService'

const BASE_URL = process.env.NODE_ENV === 'production' ? '/api/' : 'http://localhost:3000/api/'

const contextPayload = (context) => ({
    company: context.company ?? null,
    site: context.site ?? null,
    time: context.time ?? null,
    timeType: context.timeType ?? null,
    viewTimeBy: context.viewTimeBy ?? null,
    rooms: context.rooms ?? null,
    cleaners: context.cleaners ?? null,
    shift: context.shift ?? null,
    days: context.days ?? null,
    building: context.building ?? null,
    floor: context.floor ?? null,
    gender: context.gender ?? null,
    servikName: context.servikName ?? null,
    language: context.language ?? null,
})

/**
 * Send a message to the stats AI chat.
 * @param {string} message - User message
 * @param {Array<{ role: string; content: string }>} [history] - Previous messages for context
 * @param {object} [context] - Current dashboard filter so the AI queries the exact same data
 * @returns {Promise<{ reply: string; usage?: { total_tokens: number }; logId?: string }>}
 */
export async function sendChatMessage(message, history = [], context = {}) {
    return ajax.post('chat', {
        message,
        history,
        context: contextPayload(context),
    })
}

/**
 * Send a message and consume the streaming reply via callbacks.
 * @param {string} message - User message
 * @param {Array<{ role: string; content: string }>} [history]
 * @param {object} [context]
 * @param {{ onContent: (text: string) => void; onDone: (logId?: string) => void; onError: (message: string, logId?: string) => void; onToolStatus?: (tool: string) => void; signal?: AbortSignal }} handlers
 * @returns {Promise<void>}
 */
export async function sendChatMessageStream(message, history = [], context = {}, { onContent, onDone, onError, onToolStatus, signal }) {
    const res = await fetch(BASE_URL + 'chat/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal,
        body: JSON.stringify({
            message,
            history: Array.isArray(history) ? history.slice(-20) : [],
            context: contextPayload(context),
        }),
    })
    if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        onError(data.error || res.statusText || 'Request failed')
        return
    }
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let finished = false
    const finish = (fn, ...args) => {
        if (finished) return
        finished = true
        fn(...args)
    }
    try {
        while (true) {
            const { done, value } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() || ''
            for (const line of lines) {
                if (!line.startsWith('data: ')) continue
                try {
                    const payload = JSON.parse(line.slice(6))
                    if (payload.type === 'content' && payload.text) onContent(payload.text)
                    else if (payload.type === 'status' && payload.tool && onToolStatus) onToolStatus(payload.tool)
                    else if (payload.type === 'done') finish(onDone, payload.logId || null)
                    else if (payload.type === 'error') finish(onError, payload.message || 'Something went wrong.', payload.logId || null)
                } catch (_) {}
            }
        }
        if (buffer.startsWith('data: ')) {
            try {
                const payload = JSON.parse(buffer.slice(6))
                if (payload.type === 'content' && payload.text) onContent(payload.text)
                else if (payload.type === 'status' && payload.tool && onToolStatus) onToolStatus(payload.tool)
                if (payload.type === 'done') finish(onDone, payload.logId || null)
                if (payload.type === 'error') finish(onError, payload.message || 'Something went wrong.', payload.logId || null)
            } catch (_) {}
        }
        if (!finished) finish(onDone, null)
    } catch (err) {
        finish(onError, err.message || 'Connection error')
    }
}

/**
 * Submit feedback (thumbs up/down) for a chat response.
 * @param {string} logId - The chat log ID returned from the stream/response
 * @param {number} rating - 1 = thumbs down, 2 = thumbs up
 * @param {string[]} [tags] - Reason tags for negative feedback
 * @param {string} [comment] - Optional free-text comment
 * @returns {Promise<{ success: boolean }>}
 */
export async function sendChatFeedback(logId, rating, tags = [], comment = '') {
    return ajax.post('chat/feedback', { logId, rating, tags, comment })
}

export default { sendChatMessage, sendChatMessageStream, sendChatFeedback }
